import { useForm, useWatch } from 'react-hook-form'
import {
  ControlledInput,
  ControlledSelect,
  ControlledRadioGroupInput,
  GeocodeInput,
  Button,
  ButtonLink,
} from '@/components'
import { AnimatePresence, motion } from 'framer-motion'

import { countryList } from '@/lib/regions'
import { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { SEARCH_FOR_ACTIVE_RENTAL } from '@/queries'
import toast from 'react-hot-toast'
interface rentalAddressShape {
  streetName: string
  streetNumber: string
  city: string
  neighbourhood: string
  region: string
  unitNumber: string
  country: string
  postalZip: string
  listingType: string
}

interface RentalAddressStepProps {
  country: string
  lat: any
  lng: any
  handleUpdate: (data: any) => void
  handleBack?: () => void
  canGoBack?: boolean
  data?: any
}

const ExistingToast = () =>
  toast.error(
    'Looks like there is a rental listing already active at that property. If this is a mistake, please contact us.',
  )

export const RentalAddressStep = ({
  country,
  handleUpdate,
  handleBack,
  canGoBack = true,
  lat,
  lng,
  data,
}: RentalAddressStepProps) => {
  const [searchListing, { data: existingListing, loading, error, called }] = useLazyQuery(SEARCH_FOR_ACTIVE_RENTAL)
  const [showFields, setShowFields] = useState(false)
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<rentalAddressShape>({
    defaultValues: {
      country: country === 'USA' ? 'US' : 'CA',
      ...data,
    },
  })

  const setFromGeocode = (data) => {
    setShowFields(true)
    setValue('streetNumber', data.streetNumber)
    setValue('streetName', data.streetName)
    setValue('neighbourhood', data.neighbourhood)
    setValue('city', data.city)
    setValue('postalZip', data.postalZip)
    setValue('country', data.country)
    setValue('region', data.region)
  }

  const listingType = useWatch({ control, name: 'listingType' })

  const formHandler = async (data) => {
    if (showFields) {
      const result = await searchListing({
        variables: {
          streetNumber: data.streetNumber,
          streetName: data.streetName,
          city: data.city,
          country: data.country,
          postalZip: data.postalZip,
          unitNumber: data.unitNumber,
          region: data.region,
        },
      })

      if (result.data.searchForActiveRental.length) {
        ExistingToast()
      } else {
        handleUpdate(data)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(formHandler)} className="pt-2 pb-6">
      <GeocodeInput callback={setFromGeocode} lat={lat} lng={lng} />

      {showFields && (
        <>
          <AnimatePresence>
            <motion.div
              key="row1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="pt-6 mt-16 pb-6 sm:pb-12 border-t border-zinc-300  dark:border-zinc-700 "
            >
              <ControlledRadioGroupInput
                control={control}
                name="listingType"
                label="Type of Building"
                rules={{ required: true }}
                options={[
                  { name: 'house', label: 'House', description: '' },
                  { name: 'condo', label: 'Condo', description: '' },
                  { name: 'room', label: 'Room', description: '' },
                  { name: 'apartment', label: 'Apartment', description: '' },
                ]}
              />
            </motion.div>
            <motion.div
              key="row2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="sm:grid grid-cols-6 space-y-6 sm:space-y-0 sm:gap-10 pt-8 border-t border-zinc-300  dark:border-zinc-700  mb-8"
            >
              <ControlledInput
                name="unitNumber"
                control={control}
                rules={listingType === 'house' ? { required: false } : { required: true }}
                label="Unit Number"
                className="col-span-1"
                disabled={listingType === 'house'}
              />
              <ControlledInput
                name="streetNumber"
                control={control}
                rules={{ required: true }}
                label="Street Number"
                className="col-span-1"
                placeholder="100"
              />
              <ControlledInput
                name="streetName"
                control={control}
                rules={{ required: true }}
                label="Street Name"
                className="col-span-4"
                placeholder="Main Street W"
              />
            </motion.div>

            <motion.div
              key="row3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="sm:grid grid-cols-6 sm:gap-10 mb-8 space-y-6 sm:space-y-0"
            >
              <ControlledInput
                name="neighbourhood"
                control={control}
                label="Neighbourhood"
                className="col-span-2"
                placeholder="Centretown"
              />
              <ControlledInput
                name="region"
                control={control}
                rules={{ required: true }}
                label="Region"
                className="col-span-2"
                placeholder="Ontario"
              />
              <ControlledInput
                name="postalZip"
                control={control}
                rules={{ required: true }}
                label="Postal Code"
                className="col-span-2"
                placeholder="A1B 2C3"
              />
            </motion.div>

            <motion.div
              key="row4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="sm:grid grid-cols-6 sm:gap-10 mb-4"
            >
              <ControlledSelect
                name="country"
                control={control}
                rules={{ required: true }}
                label="Country"
                className="col-span-2"
                options={Object.entries(countryList).map((country) => ({
                  value: country[1],
                  label: country[1],
                }))}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="space-x-4 flex justify-end border-t border-zinc-300  dark:border-zinc-700  pt-6 sm:pt-16 mt-6 sm:mt-16"
            >
              {canGoBack && (
                <ButtonLink type="button" onClick={handleBack}>
                  Back
                </ButtonLink>
              )}
              {showFields && (
                <Button type="submit" outline>
                  Next
                </Button>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </form>
  )
}
